<!-- 
Expander for the warnings and flags dashboard widget.
-->
<template>
  <span>
    <v-list-group
      v-for="warning in warnings"
      :key="warning.type"
      :value="isExpanded(warning.type)"
      @input="expand($event, warning.type)"
    >
      <template v-slot:activator>
        <v-list-tile class="our-warning">
          <v-list-tile-content>
            <v-list-tile-title>
              <span :class="getDotClass(warning)"/>
              <span class="font-weight-bold">{{ warning.count }}</span>
              {{ getDescription(warning) }}
            </v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>
      </template>
      <template v-if="warning.count < 10">
        <v-list-tile
          v-for="provider in warning.providers"
          :key="`${warning.type}-${provider.providerId}`" 
          class="compact"
          @click="showProvider(warning.type, provider.providerId)"
        >
          <v-list-tile-content>
            <v-list-tile-title class="caption">{{ provider.name }}</v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>
      </template>
      <v-list-tile v-if="warning.count > 1" class="compact" @click="showProviders(warning.type)">
        <v-list-tile-content>
          <v-list-tile-title class="caption grey--text text--darken-1">
            Show in listing
          </v-list-tile-title>
        </v-list-tile-content>
      </v-list-tile>
    </v-list-group>
  </span>
</template>

<script>
import _ from "underscore";
import * as formatting from "@/helpers/formatting.helper";
import lookupMixins from "@/mixins/lookup.mixins";
import filterMixins from "@/mixins/filter.mixins";
import taskMixins from "@/mixins/task.mixins";
import * as lcpAction from "@/store/levy-collection-providers/action-types";
import * as lcpMutation from "@/store/levy-collection-providers/mutation-types";

export default {
  mixins: [lookupMixins, taskMixins, filterMixins],
  props: {
    warnings: {
      type: Array,
      required: true
    },
    levyCollectionId: {
      type: Number,
      required: true
    },
    informational: {
      type: Boolean,
      default: false
    }
  },
  methods: {
    showProviders(warningType) {
      this.searchForProvidersByWarningTypes([warningType]);
    },
    showProvider(warningType, providerId) {
      this.$router.push(
        `/levy-collections/${this.levyCollectionId}/providers/${providerId}${this.getTab(warningType)}`
      );
    },
    getTab(warningType) {
      switch (warningType) {
        case "IncompleteContacts":
          return "/contacts";
        case "IncompleteLevyParameters":
        case "LevyCalculationsOverridden":
        case "SubstitutedParameters":
          return "/calculation";
        case "SapReconciliationWarning":
        case "LatePaymentRequiresReview":
          return "/invoice";
        default:
          return "";
      }
    },
    showAllProviders() {
      this.searchForProvidersByWarningTypes(_(this.warnings).map(w => w.type));
    },
    searchForProvidersByWarningTypes(warningTypes) {
      this.$store.commit(lcpMutation.LIST_CLEAR_CRITERIA);
      this.$store.commit(lcpMutation.LIST_SET_CRITERIA, { levyCollectionProviderWarningTypes: warningTypes });
      this.$store.commit(lcpMutation.LIST_SET_FILTERS, { levyCollectionId: this.levyCollectionId });
      this.$store.dispatch(lcpAction.LIST);
      this.$router.push(`/levy-collections/${this.levyCollectionId}/providers`);
    },
    pluralise(count) {
      return `provider${count == 1 ? "" : "s"}`;
    },
    getDescription(warning) {
      const desc = formatting.startWithLowerCase(
        this.lookups.levyCollectionProviderWarningTypeDescriptions[warning.type]
      );
      if (desc == "invoice overdue") {
        const days = this.$store.state.levyCollections.view.data.daysAfterInvoiceIsDueToDisplayWarning;
        return `with invoice overdue ${days}+ days`;
      }
      return desc == "paused" || desc == "manually finalised" ? desc : `with ${desc}`;
    },
    isExpanded(warningType) {
      return this.$store.state.levyCollectionProviders.warnings.expanded == warningType;
    },
    expand(expanded, warningType) {
      const m = expanded ? lcpMutation.WARNINGS_SET_EXPANDED : lcpMutation.WARNINGS_SET_UNEXPANDED;
      this.$store.commit(m, warningType);
    },
    isForInformationOnly(warningType) {
      return this.doesLookupHaveAttribute(
        warningType,
        "ForInformationOnly",
        this.lookups.levyCollectionProviderWarningTypes
      );
    },
    getDotClass(warning) {
      return this.isForInformationOnly(warning.type) ? "success_dot" : "error_dot";
    }
  }
};
</script>

<style lang="scss">
.our-warning {
  height: 40px;
  .v-list__tile {
    height: 40px;
  }
  .v-list__tile__title {
    font-size: 13px;
  }
  .v-list__tile theme--light {
    padding-left: 15px;
  }
}

div.compact {
  height: 32px;
  a.v-list__tile.v-list__tile--link {
    height: 32px;
    div.v-list__tile__content {
      height: 32px;
      div.v-list__tile__title {
        margin-left: 22px;
      }
    }
  }
}
.error_dot {
  height: 15px;
  width: 15px;
  border: 2px solid var(--v-error-base);
  position: relative;
  top: 3px;
  display: inline-block;
  margin-right: 8px;
  border-radius: 50%;
  -moz-border-radius: 50%;
  -webkit-border-radius: 50%;
}
.warning_dot {
  @extend .error_dot;
  border: 2px solid var(--v-warning-base);
}
.success_dot {
  @extend .error_dot;
  border: 2px solid var(--v-success-base);
}
</style>